/**
 * Crew Payment History Page
 *
 * Shows payment history for a specific crew member.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getCrewPaymentHistory, downloadReportCSV, CrewPaymentHistoryData, CrewPaymentHistoryResponse } from '../../../api/reports';
import { getErrorMessage } from '../../../api/client';
import { ReportFilters, ReportSummaryCard, useReportDateRange, ScheduledHoursBadge } from '../../../components/reports';
import { formatInTimeZone } from '../../../utils/dateFormatting';

export default function CrewPaymentHistoryPage() {
  const { id: crewId } = useParams<{ id: string }>();
  const [dateRange, setDateRange] = useReportDateRange();
  const [data, setData] = useState<CrewPaymentHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const fetchReport = useCallback(async () => {
    if (!crewId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getCrewPaymentHistory(crewId, {
        startDate: new Date(dateRange.startDate).toISOString(),
        endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString(),
      });
      setData(response);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [crewId, dateRange]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleExport = async () => {
    if (!crewId) return;

    setIsExporting(true);
    try {
      await downloadReportCSV(
        `/reports/crew/${crewId}/history`,
        {
          startDate: new Date(dateRange.startDate).toISOString(),
          endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString(),
        },
        `crew-payment-history-${crewId}.csv`
      );
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsExporting(false);
    }
  };

  const formatCurrency = (cents: number | null) => {
    if (cents === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const formatDate = (dateString: string) => {
    return formatInTimeZone(dateString, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatRate = (rate: number | null, rateType: string | null) => {
    if (rate === null) return '-';
    const formatted = formatCurrency(rate);
    return rateType === 'FLAT' ? `${formatted} (flat)` : `${formatted}/hr`;
  };

  const getStatusBadge = (status: string) => {
    const statusStyles: Record<string, string> = {
      CONFIRMED: 'bg-green-100 text-green-800',
      ASSIGNED: 'bg-yellow-100 text-yellow-800',
      DECLINED: 'bg-red-100 text-red-800',
      REMOVED: 'bg-gray-100 text-gray-800',
    };
    return statusStyles[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div>
      <nav className="mb-4">
        <Link to="/admin/reports/payroll" className="text-primary-600 hover:text-primary-700 text-sm">
          &larr; Back to Payroll Report
        </Link>
      </nav>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {data?.crew ? `${data.crew.firstName} ${data.crew.lastName}` : 'Crew Member'} - Payment History
        </h1>
        {data?.crew && (
          <p className="text-gray-600 mt-1">{data.crew.email}</p>
        )}
      </div>

      <ReportFilters
        dateRange={dateRange}
        onChange={setDateRange}
        onExportCSV={handleExport}
        isExporting={isExporting}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Summary cards */}
      {!loading && data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <ReportSummaryCard
            label="Total Pay"
            value={formatCurrency(data.totals.totalPay)}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <ReportSummaryCard
            label="Total Hours"
            value={data.totals.totalHours.toFixed(1)}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <ReportSummaryCard
            label="Assignments"
            value={data.totals.assignmentCount.toString()}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            }
          />
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-rack border-r-transparent" />
            <p className="mt-2 text-gray-600">Loading report...</p>
          </div>
        ) : !data || data.report.length === 0 ? (
          <div className="p-8 text-center">
            <h3 className="text-sm font-medium text-gray-900">No data found</h3>
            <p className="mt-1 text-sm text-gray-500">No payment history for the selected date range</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Hours</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pay</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.report.map((row: CrewPaymentHistoryData) => (
                  <tr key={row.assignmentId} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <Link
                        to={`/admin/events/${row.eventId}`}
                        className="font-medium text-gray-900 hover:text-primary-600"
                      >
                        {row.eventName}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{formatDate(row.eventDate)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{row.roleName}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">
                      {formatRate(row.effectiveRate, row.effectiveRateType)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">
                      {row.hoursWorked !== null ? row.hoursWorked.toFixed(1) : '-'}
                      {row.hoursDefaulted && <ScheduledHoursBadge />}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                      {formatCurrency(row.totalPay)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusBadge(row.status)}`}>
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
